import { matchPath } from 'react-router-dom';
import { isNavItemVisible, NAV_ITEMS } from '../layout/navItems';
import { CurrentUser } from '../types/auth';
import { userHasPermission } from './permissions';

/** Deep-link-only routes (not in the sidebar's NAV_ITEMS, since they're
 * reached by drilling into a list rather than a nav click) — each maps to
 * the same permission its parent list page already requires in App.tsx.
 * Kept as a short, explicit list rather than trying to derive it from the
 * <Routes> JSX tree, so this stays simple to audit against App.tsx by eye. */
const DEEP_LINK_ROUTE_PERMISSIONS: Array<{
  pattern: string;
  module: string;
  action: string;
  group?: 'Administration' | 'Finance';
  /** Only these roles, whatever their permissions. */
  roles?: string[];
}> = [
  { pattern: '/leads/:id', module: 'leads', action: 'view' },
  { pattern: '/students/:id', module: 'students', action: 'view' },
  { pattern: '/finance/students/:id', module: 'finance', action: 'view', group: 'Finance' },
  { pattern: '/master-data/*', module: 'master_data', action: 'view', group: 'Administration', roles: ['Super Admin'] },
];

/** Routes reachable by any authenticated user regardless of permission
 * (no RequirePermission wrapper in App.tsx). */
const OPEN_ROUTES = ['/change-password', '/my-account'];

/** Login-redirect correction: whether `pathname` is a route the given user
 * is actually authorized to land on, checked against the exact same
 * module:action pairs App.tsx's <RequirePermission> wrappers enforce (not
 * re-implemented separately — NAV_ITEMS already carries this for every
 * top-level page; DEEP_LINK_ROUTE_PERMISSIONS covers the handful of
 * detail/nested routes that aren't in the sidebar). An unrecognized
 * pathname is treated as unauthorized (the safe default), not as open. */
export const isRouteAuthorized = (pathname: string, user: CurrentUser | null): boolean => {
  // Student Portal (Phase 67) — a Student holds zero staff module:action
  // permissions (see permissions.ts's ROLE_PERMISSION_DEFAULTS), so every
  // staff route below would already correctly return false for them; this
  // is only here to make `/portal/*` itself authorized, since it isn't in
  // NAV_ITEMS/DEEP_LINK_ROUTE_PERMISSIONS (it has no staff sidebar entry
  // or permission at all).
  if (user?.role.name === 'Student') return pathname.startsWith('/portal');

  if (OPEN_ROUTES.some((route) => matchPath(route, pathname))) return true;

  const navItem = NAV_ITEMS.find((item) => matchPath(item.path, pathname));
  if (navItem) {
    return (
      (!navItem.permission || userHasPermission(user, navItem.permission.module, navItem.permission.action)) &&
      isNavItemVisible(navItem, user?.role.name)
    );
  }

  const deepLink = DEEP_LINK_ROUTE_PERMISSIONS.find((route) => matchPath(route.pattern, pathname));
  if (deepLink) {
    return (
      userHasPermission(user, deepLink.module, deepLink.action) &&
      // An ungrouped deep link (/leads/:id, /students/:id) counts as CRM, so
      // a Finance-only role is refused it like any non-Finance page.
      isNavItemVisible({ group: deepLink.group ?? 'CRM', roles: deepLink.roles }, user?.role.name)
    );
  }

  return false;
};

/** The safe post-login landing page: the first NAV_ITEMS entry (in the
 * sidebar's own declared order — Dashboard first) this user is actually
 * authorized for, never a hard-coded route. Falls back to /change-password
 * (open to any authenticated user) in the pathological case where a role
 * holds literally no module:view permission at all. */
export const getDefaultRoute = (user: CurrentUser | null): string => {
  // Student Portal (Phase 67) — a Student's landing page is never any
  // staff NAV_ITEMS entry (they hold no permission for any of them); this
  // must be checked before the NAV_ITEMS search below, which would
  // otherwise fall through to /change-password for lack of any match.
  if (user?.role.name === 'Student') return '/portal';

  const firstAuthorized = NAV_ITEMS.find(
    (item) =>
      (!item.permission || userHasPermission(user, item.permission.module, item.permission.action)) &&
      isNavItemVisible(item, user?.role.name),
  );
  return firstAuthorized?.path ?? '/change-password';
};
